/**
 * drawer.js — Accessible Bottom Sheet / Side Panel Drawer Web Component
 * For Shopify 2.0 Themes (.liquid + vanilla JS)
 *
 * ─── QUICK START ────────────────────────────────────────────────────────────
 *
 * 1. Drop this file into your theme's /assets folder.
 *
 * 2. Load it in theme.liquid (before </body>):
 *      <script src="{{ 'drawer.js' | asset_url }}" defer></script>
 *
 * 3. Write your markup in any .liquid file:
 *
 *    <drawer-root id="cart-drawer">
 *
 *      <drawer-trigger>
 *        <button type="button">Open Cart</button>
 *      </drawer-trigger>
 *
 *      <drawer-backdrop></drawer-backdrop>
 *
 *      <drawer-panel>
 *        <drawer-title>Your Cart</drawer-title>
 *        <drawer-description>3 items</drawer-description>
 *
 *        <!-- your section content here -->
 *
 *        <drawer-close>
 *          <button type="button" aria-label="Close cart">×</button>
 *        </drawer-close>
 *      </drawer-panel>
 *
 *    </drawer-root>
 *
 *
 * ─── ELEMENTS ────────────────────────────────────────────────────────────────
 *
 *  <drawer-root>         State controller. Parent of all other parts.
 *  <drawer-trigger>      Wrap your <button> or <a>. Stays in original DOM position.
 *  <drawer-backdrop>     Overlay behind the panel. Portals to <body> on init.
 *  <drawer-panel>        The sliding sheet. Portals to <body> on init.
 *  <drawer-handle>       Drag target for swipe-to-dismiss. Must be first child of <drawer-panel>.
 *                        Sets touch-action:none so the browser does not claim the gesture.
 *                        Without this element swipe-to-dismiss is disabled.
 *  <drawer-title>        Visible heading; auto-wired to aria-labelledby.
 *  <drawer-description>  Supporting text; auto-wired to aria-describedby.
 *  <drawer-close>        Wrap your close <button>. Can live anywhere inside the panel.
 *
 *
 * ─── ATTRIBUTES (<drawer-root>) ──────────────────────────────────────────────
 *
 *  open                       Boolean. Reflects open state. Set to open on page load.
 *  modal="false"              Disables focus trap, scroll lock, and backdrop dismiss.
 *  disable-pointer-dismissal  Prevents clicking the backdrop from closing.
 *
 *
 * ─── JAVASCRIPT API ──────────────────────────────────────────────────────────
 *
 *  const drawer = document.getElementById('cart-drawer');
 *  await drawer.open();
 *  await drawer.close();
 *  drawer.toggle();
 *  drawer.isOpen   // → boolean
 *
 *  // Listen from anywhere
 *  document.addEventListener('drawer-open',  (e) => console.log('opened', e.target.id));
 *  document.addEventListener('drawer-close', (e) => console.log('closed', e.target.id));
 *
 *  // Open a drawer from any button, anywhere on the page
 *  <button data-drawer-open="cart-drawer">Open cart from header</button>
 *
 *
 * ─── DATA ATTRIBUTES (set by component — use as CSS hooks) ───────────────────
 *
 *  [data-open]                   On <drawer-panel> and <drawer-backdrop> while open.
 *  [data-starting-style]         Briefly applied on open. Set your "off-screen" position here.
 *  [data-ending-style]           Applied during close. Set your "off-screen" position here.
 *  [data-swiping]                On <drawer-panel> during an active swipe gesture.
 *  [data-nested-drawer-open]     On <drawer-panel> (and its portal div) while a child
 *                                drawer is stacked above it.
 *  [data-nested-drawer-swiping]  On <drawer-panel> while a child drawer is mid-swipe.
 *
 *
 * ─── CSS CUSTOM PROPERTIES (live on <drawer-panel>) ──────────────────────────
 *
 *  --drawer-swipe-movement-y    Pixel distance of current swipe (positive = downward).
 *  --drawer-swipe-progress      0–1. How far through a dismiss gesture.
 *  --drawer-swipe-strength      0–1. Velocity factor. Use to scale exit duration:
 *                               transition-duration: calc(var(--drawer-swipe-strength) * 400ms)
 *  --nested-drawers             Integer count of open nested drawers stacked above this one.
 *  --drawer-height              Live pixel height of this panel (set via ResizeObserver).
 *  --drawer-frontmost-height    Pixel height of the topmost open panel in the stack.
 *                               Falls back to --drawer-height when this IS the frontmost.
 *
 *
 * ─── SWIPE IGNORE ────────────────────────────────────────────────────────────
 *
 *  Add data-drawer-swipe-ignore to any descendant to prevent it from
 *  initiating swipe-to-dismiss. Useful for inner scroll areas, maps, etc.
 *
 *    <div class="product-gallery" data-drawer-swipe-ignore>...</div>
 *
 *
 * ─── NESTED DRAWERS ──────────────────────────────────────────────────────────
 *
 *  Open a second <drawer-root> from inside a <drawer-panel>. The component
 *  automatically detects the parent relationship at open time, increments
 *  --nested-drawers on every ancestor panel, and propagates
 *  --drawer-frontmost-height so parent panels can scale / peek correctly.
 *
 *  Example CSS for a right-side panel stacking effect:
 *
 *  drawer-panel {
 *    --peek: 1rem;
 *    --stack-step: 0.05;
 *    --stack-progress: clamp(0, var(--drawer-swipe-progress, 0), 1);
 *    --stack-peek-offset: max(0px, calc(
 *        (var(--nested-drawers, 0) - var(--stack-progress)) * var(--peek)));
 *    --stack-scale-base: max(0, calc(1 - (var(--nested-drawers, 0) * var(--stack-step))));
 *    --stack-scale: clamp(0, calc(
 *        var(--stack-scale-base) + (var(--stack-step) * var(--stack-progress))), 1);
 *    transform-origin: center center;
 *    transform:
 *      translateX(calc(var(--drawer-swipe-movement-x, 0px) - var(--stack-peek-offset)))
 *      scale(var(--stack-scale));
 *  }
 *  drawer-panel[data-nested-drawer-open] {
 *    overflow: hidden;
 *  }
 *  drawer-panel[data-nested-drawer-swiping] {
 *    transition-duration: 0ms;
 *  }
 *
 *
 * ─── ACCESSIBILITY ────────────────────────────────────────────────────────────
 *
 *  ✓  role="dialog" + aria-modal="true" on <drawer-panel>
 *  ✓  aria-labelledby auto-connected from <drawer-title>
 *  ✓  aria-describedby auto-connected from <drawer-description>
 *  ✓  aria-expanded + aria-controls on the trigger <button>
 *  ✓  Focus trap: Tab / Shift+Tab cycles within the open panel
 *  ✓  Escape key closes
 *  ✓  Focus returns to the triggering element on close
 *  ✓  Body scroll lock while open (modal mode)
 *  ✓  inert applied to all background content while open
 *
 *
 * ─── EXAMPLE CSS ──────────────────────────────────────────────────────────────
 *
 * Paste this into your theme CSS as a starting point. Every transition
 * and visual detail is yours to control — the component only manages
 * behaviour, ARIA, and the data attributes / CSS variables above.
 *
 *  [data-drawer-portal] { display: contents; }
 *
 *  drawer-backdrop {
 *    position: fixed;
 *    inset: 0;
 *    min-height: 100dvh;
 *    background-color: #000;
 *    --backdrop-opacity: 0.35;
 *    opacity: calc(var(--backdrop-opacity) * (1 - var(--drawer-swipe-progress, 0)));
 *    transition: opacity 450ms cubic-bezier(0.32, 0.72, 0, 1);
 *  }
 *  drawer-backdrop[data-starting-style],
 *  drawer-backdrop[data-ending-style]   { opacity: 0; }
 *  drawer-backdrop[data-swiping]        { transition-duration: 0ms; }
 *  drawer-backdrop[data-ending-style]   {
 *    transition-duration: calc(var(--drawer-swipe-strength, 1) * 400ms);
 *  }
 *
 *  drawer-panel {
 *    --bleed: 3rem;
 *    box-sizing: border-box;
 *    position: fixed;
 *    inset-inline: 0;
 *    bottom: 0;
 *    margin-bottom: calc(-1 * var(--bleed));
 *    max-height: calc(85vh + var(--bleed));
 *    padding: 1.5rem 1.5rem
 *             calc(1.5rem + env(safe-area-inset-bottom, 0px) + var(--bleed));
 *    background: #fff;
 *    border-radius: 1rem 1rem 0 0;
 *    box-shadow: 0 -4px 24px rgb(0 0 0 / 0.1);
 *    overflow-y: auto;
 *    overscroll-behavior: contain;
 *    touch-action: auto;
 *    transform: translateY(var(--drawer-swipe-movement-y, 0));
 *    transition: transform 450ms cubic-bezier(0.32, 0.72, 0, 1);
 *    will-change: transform;
 *  }
 *  drawer-panel[data-starting-style],
 *  drawer-panel[data-ending-style] {
 *    transform: translateY(calc(100% - var(--bleed) + 2px));
 *  }
 *  drawer-panel[data-swiping]     { transition-duration: 0ms; user-select: none; }
 *  drawer-panel[data-ending-style] {
 *    transition-duration: calc(var(--drawer-swipe-strength, 1) * 400ms);
 *  }
 */

(() => {
  // ─── Utilities ──────────────────────────────────────────────────────────────

  let _uidCounter = 0;
  const uid = (/** @type {string} */ prefix) => `${prefix}-${++_uidCounter}`;

  /**
   * Wait for the longest transition on `el` to finish.
   * Uses a computed-duration timeout as a reliable fallback for cases where
   * transitionend doesn't fire (e.g. zero-duration, display:none race).
   * @param {HTMLElement} el
   * @returns {Promise<void>}
   */
  function waitForTransition(el) {
    return new Promise((resolve) => {
      const cs = getComputedStyle(el);
      const durations = cs.transitionDuration.split(",").map(Number.parseFloat);
      const delays = cs.transitionDelay.split(",").map(Number.parseFloat);

      let max = 0;
      for (let i = 0; i < durations.length; i++) {
        max = Math.max(max, (durations[i] || 0) + (delays[i] || 0));
      }
      if (max <= 0) {
        resolve();
        return;
      }

      const timer = setTimeout(resolve, max * 1000 + 20);
      el.addEventListener("transitionend", function handler(e) {
        if (e.target !== el) {
          return;
        }
        clearTimeout(timer);
        el.removeEventListener("transitionend", handler);
        resolve();
      });
    });
  }

  /**
   * Two rAF ticks so the browser paints the data-starting-style position
   * before we remove it, triggering the CSS transition.
   * @returns {Promise<void>}
   */
  const nextTick = () =>
    new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );

  /**
   * Returns keyboard-focusable descendants of `container`,
   * skipping elements inside [hidden] or [inert] subtrees.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getFocusable(container) {
    return /** @type {HTMLElement[]} */ (
      Array.from(
        container.querySelectorAll(
          "a[href], button:not([disabled]), input:not([disabled]), " +
            "select:not([disabled]), textarea:not([disabled]), " +
            '[tabindex]:not([tabindex="-1"]), details > summary'
        )
      ).filter((el) => !(el.closest("[hidden]") || el.closest("[inert]")))
    );
  }

  // ─── Nesting support ─────────────────────────────────────────────────────────
  //
  //  Maps child DrawerRoot → parent DrawerRoot (seeded at open time).
  //  Using a WeakMap means closed / removed drawers are GC'd automatically.
  //

  /** @type {WeakMap<DrawerRoot, DrawerRoot>} */
  const drawerParents = new WeakMap();

  // Capture the most recent pointer-down target so programmatic opens
  // (where there is no <drawer-trigger>) can still detect a parent panel.
  /** @type {Element | null} */
  let _lastPointerTarget = null;
  document.addEventListener(
    "pointerdown",
    (e) => {
      _lastPointerTarget = /** @type {Element} */ (e.target);
    },
    true
  );

  /**
   * Walk every open ancestor of `root` via drawerParents and invoke `fn` on each.
   * @param {DrawerRoot} root
   * @param {(ancestor: DrawerRoot) => void} fn
   */
  function walkAncestors(root, fn) {
    let cur = drawerParents.get(root);
    while (cur) {
      fn(cur);
      cur = drawerParents.get(cur);
    }
  }

  /**
   * Propagate --drawer-frontmost-height to every ancestor panel so they can
   * compute scale / peek-offset transforms.
   * @param {DrawerRoot} frontmostRoot  The newly open (topmost) drawer.
   */
  function propagateFrontmostHeight(frontmostRoot) {
    const panel = frontmostRoot._panel;
    if (!panel) {
      return;
    }
    const h = `${panel.offsetHeight}px`;
    walkAncestors(frontmostRoot, (a) => {
      a._panel?.style.setProperty("--drawer-frontmost-height", h);
    });
  }

  // ─── Direction config ────────────────────────────────────────────────────────
  //
  //  Maps swipe-direction attribute values to the axis, CSS custom property,
  //  and sign used for both swipe-to-open (DrawerSwipeArea) and
  //  swipe-to-dismiss (DrawerPanel).
  //
  //  openSign: the sign of the raw pointer delta that moves the panel TOWARD open.
  //    'up'    → drag upward   = negative dy → openSign -1
  //    'down'  → drag downward = positive dy → openSign  1
  //    'left'  → drag leftward = negative dx → openSign -1
  //    'right' → drag rightward= positive dx → openSign  1

  /**
   * @param {string | null} dir
   * @returns {{ axis: 'x' | 'y', cssVar: string, openSign: 1 | -1 }}
   */
  function getDirectionConfig(dir) {
    switch (dir) {
      case "down":
        return { axis: "y", cssVar: "--drawer-swipe-movement-y", openSign: 1 };
      case "left":
        return { axis: "x", cssVar: "--drawer-swipe-movement-x", openSign: -1 };
      case "right":
        return { axis: "x", cssVar: "--drawer-swipe-movement-x", openSign: 1 };
      default: // 'up'
        return { axis: "y", cssVar: "--drawer-swipe-movement-y", openSign: -1 };
    }
  }

  // ─── DrawerRoot ─────────────────────────────────────────────────────────────
  //
  //  The state controller. Owns the open/close lifecycle, focus management,
  //  scroll lock, and portal creation. All other elements report to this one.
  //

  class DrawerRoot extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._isAnimating = false;
      /** @type {HTMLElement | null} */
      this._portal = null; // <div data-drawer-portal> appended to <body>
      /** @type {HTMLElement | null} */
      this._panel = null; // <drawer-panel>
      /** @type {HTMLElement | null} */
      this._backdrop = null; // <drawer-backdrop>
      /** @type {HTMLElement | null} */
      this._triggerBtn = null; // The <button>/<a> we stamp aria-expanded on
      /** @type {HTMLElement | null} */
      this._prevFocus = null; // Element to restore focus to on close
      /** @type {HTMLElement[]} */
      this._inertEls = []; // Body children we've marked inert while open
      /** @type {((e: KeyboardEvent) => void) | null} */
      this._keyHandler = null;
      this._nestedCount = 0; // How many child drawers are currently open above this one
      this._isPeeking = false; // True while DrawerSwipeArea mid-gesture preview
    }

    static get observedAttributes() {
      return ["open"];
    }

    get isOpen() {
      return this._isOpen;
    }
    get modal() {
      return this.getAttribute("modal") !== "false";
    }
    get disablePointerDismissal() {
      return this.hasAttribute("disable-pointer-dismissal");
    }
    get swipeDirection() {
      return this.getAttribute("swipe-direction") ?? "up";
    }

    connectedCallback() {
      // Defer one microtask so all children are parsed before we query them.
      Promise.resolve().then(() => this._init());
    }

    _init() {
      this._panel = /** @type {HTMLElement | null} */ (
        this.querySelector("drawer-panel")
      );
      this._backdrop = /** @type {HTMLElement | null} */ (
        this.querySelector("drawer-backdrop")
      );
      const trigger = this.querySelector("drawer-trigger");

      if (!this._panel) {
        console.warn("<drawer-root>: No <drawer-panel> child found.");
        return;
      }

      // ── Build portal in <body> ────────────────────────────────────────────
      this._portal = document.createElement("div");
      this._portal.setAttribute("data-drawer-portal", "");
      document.body.appendChild(this._portal);

      // Physically move backdrop + panel into the portal. This decouples them
      // from the stacking context of whatever section they were authored in.
      if (this._backdrop) {
        this._portal.appendChild(this._backdrop);
        /** @type {any} */ (this._backdrop)._root = this;
      }
      this._portal.appendChild(this._panel);
      /** @type {any} */ (this._panel)._root = this;

      // ── Auto-wire ARIA IDs ────────────────────────────────────────────────
      if (!this._panel.id) {
        this._panel.id = uid("drawer-panel");
      }

      const titleEl = this._panel.querySelector("drawer-title");
      if (titleEl) {
        if (!titleEl.id) {
          titleEl.id = uid("drawer-title");
        }
        this._panel.setAttribute("aria-labelledby", titleEl.id);
      }

      const descEl = this._panel.querySelector("drawer-description");
      if (descEl) {
        if (!descEl.id) {
          descEl.id = uid("drawer-description");
        }
        this._panel.setAttribute("aria-describedby", descEl.id);
      }

      // ── Trigger ARIA ──────────────────────────────────────────────────────
      // Prefer stamping aria-* on an inner <button> or <a> if one exists,
      // otherwise use the <drawer-trigger> element itself.
      if (trigger) {
        this._triggerBtn = /** @type {HTMLElement} */ (
          trigger.querySelector("button, a[href]") ?? trigger
        );
        this._triggerBtn.setAttribute("aria-controls", this._panel.id);
        this._triggerBtn.setAttribute("aria-expanded", "false");
        /** @type {any} */ (trigger)._root = this;
      }

      // ── Start hidden ──────────────────────────────────────────────────────
      this._panel.setAttribute("hidden", "");
      this._panel.setAttribute("aria-hidden", "true");
      if (this._backdrop) {
        this._backdrop.setAttribute("hidden", "");
      }

      // ── Honour open attribute present at parse time ───────────────────────
      if (this.hasAttribute("open")) {
        this._open(/* immediate */ true);
      }
    }

    // ── Public API ────────────────────────────────────────────────────────────

    open() {
      return this._open();
    }
    close() {
      return this._close();
    }
    toggle() {
      this._isOpen ? this.close() : this.open();
    }

    // ── Nesting helpers ───────────────────────────────────────────────────────

    /**
     * Find the DrawerRoot whose panel contains the element that triggered
     * this open(). Checks the declared trigger button first, then the
     * currently active element, then the last pointer-down target — the last
     * two cover programmatic opens where no <drawer-trigger> is present.
     * @returns {DrawerRoot | null}
     */
    _findParentRoot() {
      const candidates = [
        this._triggerBtn,
        /** @type {Element | null} */ (document.activeElement),
        _lastPointerTarget,
      ];
      for (const el of candidates) {
        if (!el) {
          continue;
        }
        const parentPanel = el.closest("drawer-panel");
        const parentRoot = /** @type {DrawerRoot | undefined} */ (
          /** @type {any} */ (parentPanel)?._root
        );
        if (parentRoot?.isOpen) {
          return parentRoot;
        }
      }
      return null;
    }

    /** Called on every ancestor when a child drawer opens above them. */
    _incrementNested() {
      this._nestedCount++;
      if (this._panel) {
        this._panel.setAttribute("data-nested-drawer-open", "");
        this._panel.style.setProperty(
          "--nested-drawers",
          String(this._nestedCount)
        );
      }
      this._portal?.setAttribute("data-nested-drawer-open", "");
    }

    /** Called on every ancestor when a child drawer closes. */
    _decrementNested() {
      this._nestedCount = Math.max(0, this._nestedCount - 1);
      if (this._panel) {
        this._panel.style.setProperty(
          "--nested-drawers",
          String(this._nestedCount)
        );
        if (this._nestedCount === 0) {
          this._panel.removeAttribute("data-nested-drawer-open");
        }
      }
      if (this._nestedCount === 0) {
        this._portal?.removeAttribute("data-nested-drawer-open");
      }
    }

    // ── Internal: open ────────────────────────────────────────────────────────

    async _open(immediate = false) {
      if (this._isOpen || this._isAnimating) {
        return;
      }
      this._isAnimating = true;

      this._isOpen = true;
      this.setAttribute("open", "");
      if (this._triggerBtn) {
        this._triggerBtn.setAttribute("aria-expanded", "true");
      }

      // Un-hide before animation begins. Also remove inert from the portal:
      // a parent modal drawer may have inerted it via _setPageInert.
      this._portal?.removeAttribute("inert");
      this._panel?.removeAttribute("hidden");
      this._panel?.removeAttribute("aria-hidden");
      if (this._backdrop) {
        this._backdrop.removeAttribute("hidden");
      }

      // Apply data-open + data-starting-style in the same frame so CSS sees
      // both simultaneously. The panel's CSS should read:
      //   normal state             → final "open" position
      //   [data-starting-style]    → off-screen "start" position
      // We then strip starting-style to fire the transition.
      this._panel?.setAttribute("data-open", "");
      this._panel?.setAttribute("data-starting-style", "");
      if (this._backdrop) {
        this._backdrop.setAttribute("data-open", "");
        this._backdrop.setAttribute("data-starting-style", "");
      }

      if (immediate) {
        // No animation — e.g. drawer is open on page load
        this._panel?.removeAttribute("data-starting-style");
        if (this._backdrop) {
          this._backdrop.removeAttribute("data-starting-style");
        }
      } else {
        // Force a reflow so the browser paints the starting position first,
        // then strip it to trigger the CSS transition into the open position.
        this._panel?.getBoundingClientRect();
        await nextTick();
        this._panel?.removeAttribute("data-starting-style");
        if (this._backdrop) {
          this._backdrop.removeAttribute("data-starting-style");
        }
      }

      if (this.modal) {
        this._lockScroll();
        this._setPageInert(true);
      }

      // ── Nesting: register with parent and notify all ancestors ────────────
      const parent = this._findParentRoot();
      if (parent) {
        drawerParents.set(this, parent);
        walkAncestors(this, (a) => a._incrementNested());
        propagateFrontmostHeight(this);
      }

      this._attachKeyHandlers();
      this._focusIn();

      this._isAnimating = false;
      this.dispatchEvent(
        new CustomEvent("drawer-open", { bubbles: true, composed: true })
      );
    }

    // ── Internal: close ───────────────────────────────────────────────────────

    async _close(
      { swipeDuration } = /** @type {{ swipeDuration?: number }} */ ({})
    ) {
      if (!this._isOpen || this._isAnimating) {
        return;
      }
      this._isAnimating = true;

      // Remove data-open and add data-ending-style simultaneously.
      // CSS: [data-ending-style] should define the off-screen end position.
      // The transition fires from the current "open" position to that state.
      this._panel?.removeAttribute("data-open");
      this._panel?.setAttribute("data-ending-style", "");
      if (this._backdrop) {
        this._backdrop.removeAttribute("data-open");
        this._backdrop.setAttribute("data-ending-style", "");
      }

      // ── Nesting: notify ancestors immediately so they animate concurrently ─
      // Decrement before awaiting so the parent panel's un-scale transition
      // runs at the same time as this drawer's exit, not sequentially after.
      walkAncestors(this, (a) => a._decrementNested());

      // Wait for the exit animation. When dismissing via swipe, use the
      // velocity-derived duration instead of the computed CSS duration
      // (since --drawer-swipe-strength modifies it via calc() in CSS,
      // which getComputedStyle can't read through a CSS variable).
      await (swipeDuration == null
        ? waitForTransition(/** @type {HTMLElement} */ (this._panel))
        : new Promise((r) => setTimeout(r, swipeDuration + 20)));

      // Fully hide
      this._panel?.setAttribute("hidden", "");
      this._panel?.setAttribute("aria-hidden", "true");
      if (this._backdrop) {
        this._backdrop.setAttribute("hidden", "");
      }

      // Strip lingering data attrs and CSS vars
      this._panel?.removeAttribute("data-ending-style");
      this._panel?.removeAttribute("data-swiping");
      if (this._backdrop) {
        this._backdrop.removeAttribute("data-ending-style");
      }
      this._clearSwipeProps();

      // ── Nesting: clean up parent height and mapping ────────────────────────
      const parent = drawerParents.get(this);
      if (parent) {
        // Parent is now the frontmost — let it use its own --drawer-height
        parent._panel?.style.removeProperty("--drawer-frontmost-height");
        // Propagate parent's height to any grandparent panels
        propagateFrontmostHeight(parent);
      }
      drawerParents.delete(this);

      this._isOpen = false;
      this.removeAttribute("open");
      if (this._triggerBtn) {
        this._triggerBtn.setAttribute("aria-expanded", "false");
      }

      if (this.modal) {
        this._unlockScroll();
        this._setPageInert(false);
      }

      this._detachKeyHandlers();
      this._focusOut();

      this._isAnimating = false;
      this.dispatchEvent(
        new CustomEvent("drawer-close", { bubbles: true, composed: true })
      );
    }

    // ── Scroll lock ────────────────────────────────────────────────────────────

    _lockScroll() {
      // Compensate for scrollbar width to prevent layout shift
      const scrollbarW =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      if (scrollbarW > 0) {
        document.body.style.paddingRight = `${scrollbarW}px`;
      }
    }

    _unlockScroll() {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    // ── inert (background suppression) ────────────────────────────────────────

    _setPageInert(on) {
      const nonVisualTags = new Set([
        "SCRIPT",
        "STYLE",
        "LINK",
        "META",
        "NOSCRIPT",
        "TEMPLATE",
      ]);
      if (on) {
        this._inertEls = /** @type {HTMLElement[]} */ (
          Array.from(document.body.children).filter(
            (el) =>
              !(
                el.hasAttribute("data-drawer-portal") ||
                nonVisualTags.has(el.tagName)
              )
          )
        );
        this._inertEls.forEach((el) => el.setAttribute("inert", ""));
      } else {
        this._inertEls.forEach((el) => el.removeAttribute("inert"));
        this._inertEls = [];
      }
    }

    // ── Focus management ──────────────────────────────────────────────────────

    _focusIn() {
      this._prevFocus = /** @type {HTMLElement} */ (document.activeElement);
      // rAF ensures the panel is painted and transitioned before we focus into it
      const target =
        (this._panel ? getFocusable(this._panel)[0] : null) ?? this._panel;
      requestAnimationFrame(() => target?.focus());
    }

    _focusOut() {
      this._prevFocus?.focus();
      this._prevFocus = null;
    }

    // ── Keyboard handlers ─────────────────────────────────────────────────────

    _attachKeyHandlers() {
      this._keyHandler = (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          this.close();
          return;
        }
        if (e.key === "Tab") {
          this._handleTab(e);
        }
      };
      document.addEventListener("keydown", this._keyHandler);
    }

    _detachKeyHandlers() {
      if (this._keyHandler) {
        document.removeEventListener("keydown", this._keyHandler);
        this._keyHandler = null;
      }
    }

    /** @param {KeyboardEvent} e */
    _handleTab(e) {
      if (!this._panel) {
        return;
      }
      const focusable = getFocusable(this._panel);
      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        // Shift+Tab from first (or from outside panel) → wrap to last
        if (active === first || !this._panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab from last (or from outside panel) → wrap to first
        if (active === last || !this._panel.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    // ── Swipe-to-open (called by DrawerSwipeArea) ────────────────────────────

    /**
     * Called on every pointermove from DrawerSwipeArea.
     * rawDelta is signed along the open axis (e.g. negative = dragging up).
     * @param {number} rawDelta
     */
    _peekOpen(rawDelta) {
      if (this._isOpen || this._isAnimating) {
        return;
      }

      const cfg = getDirectionConfig(this.swipeDirection);
      const openMovement = rawDelta * cfg.openSign; // positive = toward open
      if (openMovement <= 0) {
        return;
      }

      if (!this._isPeeking) {
        this._isPeeking = true;
        this._portal?.removeAttribute("inert");
        this._panel?.removeAttribute("hidden");
        this._panel?.removeAttribute("aria-hidden");
        if (this._backdrop) {
          this._backdrop.removeAttribute("hidden");
        }

        // Prime data-open + data-starting-style simultaneously so the CSS
        // starting position (off-screen) is painted before we set data-peeking.
        this._panel?.setAttribute("data-open", "");
        this._panel?.setAttribute("data-starting-style", "");
        this._panel?.setAttribute("data-swiping", "");
        if (this._backdrop) {
          this._backdrop.setAttribute("data-open", "");
          this._backdrop.setAttribute("data-starting-style", "");
        }

        // Force reflow so the browser paints the off-screen position first.
        this._panel?.getBoundingClientRect();

        // data-peeking disables transitions so the panel tracks the finger exactly.
        this._panel?.setAttribute("data-peeking", "");
      }

      const cfg2 = getDirectionConfig(this.swipeDirection);
      const panelSize =
        cfg2.axis === "y"
          ? this._panel?.offsetHeight || 1
          : this._panel?.offsetWidth || 1;
      const progress = +Math.min(1, openMovement / panelSize).toFixed(4);

      this._panel?.style.setProperty(
        "--drawer-peek-movement",
        `${openMovement}px`
      );
      this._panel?.style.setProperty(
        "--drawer-swipe-progress",
        String(progress)
      );
      if (this._backdrop) {
        this._backdrop.style.setProperty(
          "--drawer-swipe-progress",
          String(progress)
        );
      }
    }

    /**
     * Called on pointerup from DrawerSwipeArea.
     * Commits the open if threshold is met, otherwise cancels.
     * @param {number} velocity  px/ms, positive = toward open
     * @param {number} openMovement  px dragged toward open (positive)
     */
    async _commitOpen(velocity, openMovement) {
      if (!this._isPeeking) {
        return;
      }

      const cfg = getDirectionConfig(this.swipeDirection);
      const panelSize =
        cfg.axis === "y"
          ? this._panel?.offsetHeight || 1
          : this._panel?.offsetWidth || 1;
      const progress = openMovement / panelSize;
      const shouldOpen =
        progress > 0.35 || (velocity > 0.4 && openMovement > 40);

      if (!shouldOpen) {
        this._cancelPeek();
        return;
      }

      this._isPeeking = false;
      this._isAnimating = true;
      this._isOpen = true;
      this.setAttribute("open", "");
      if (this._triggerBtn) {
        this._triggerBtn.setAttribute("aria-expanded", "true");
      }

      // Re-enable transitions, clear drag vars, then strip data-starting-style
      // to trigger the CSS transition from the peek position to the open position.
      this._panel?.removeAttribute("data-peeking");
      this._panel?.removeAttribute("data-swiping");
      this._panel?.style.removeProperty("--drawer-peek-movement");
      this._panel?.style.removeProperty("--drawer-swipe-progress");
      if (this._backdrop) {
        this._backdrop.style.removeProperty("--drawer-swipe-progress");
      }

      await nextTick();
      this._panel?.removeAttribute("data-starting-style");
      if (this._backdrop) {
        this._backdrop.removeAttribute("data-starting-style");
      }

      await waitForTransition(/** @type {HTMLElement} */ (this._panel));

      if (this.modal) {
        this._lockScroll();
        this._setPageInert(true);
      }

      const parent = this._findParentRoot();
      if (parent) {
        drawerParents.set(this, parent);
        walkAncestors(this, (a) => a._incrementNested());
        propagateFrontmostHeight(this);
      }

      this._attachKeyHandlers();
      this._focusIn();
      this._isAnimating = false;
      this.dispatchEvent(
        new CustomEvent("drawer-open", { bubbles: true, composed: true })
      );
    }

    /**
     * Called when swipe-to-open threshold is not met.
     * Snaps the panel back off-screen.
     */
    _cancelPeek() {
      if (!this._isPeeking) {
        return;
      }
      this._isPeeking = false;
      this._isAnimating = true;

      // Remove data-peeking + data-swiping to re-enable the CSS transition.
      // data-starting-style is still set, so the CSS rule for the off-screen
      // position applies and the transition fires from the current peek position.
      this._panel?.removeAttribute("data-peeking");
      this._panel?.removeAttribute("data-swiping");
      this._panel?.style.removeProperty("--drawer-peek-movement");
      this._panel?.style.removeProperty("--drawer-swipe-progress");
      if (this._backdrop) {
        this._backdrop.style.removeProperty("--drawer-swipe-progress");
      }

      waitForTransition(/** @type {HTMLElement} */ (this._panel)).then(() => {
        this._panel?.setAttribute("hidden", "");
        this._panel?.setAttribute("aria-hidden", "true");
        if (this._backdrop) {
          this._backdrop.setAttribute("hidden", "");
        }
        this._panel?.removeAttribute("data-open");
        this._panel?.removeAttribute("data-starting-style");
        if (this._backdrop) {
          this._backdrop.removeAttribute("data-open");
          this._backdrop.removeAttribute("data-starting-style");
        }
        this._isAnimating = false;
      });
    }

    // ── Swipe prop cleanup ────────────────────────────────────────────────────

    _clearSwipeProps() {
      const props = [
        "--drawer-swipe-movement-y",
        "--drawer-swipe-movement-x",
        "--drawer-swipe-progress",
        "--drawer-swipe-strength",
        "--drawer-peek-movement",
      ];
      props.forEach((p) => {
        this._panel?.style.removeProperty(p);
        this._backdrop?.style.removeProperty(p);
      });
    }

    disconnectedCallback() {
      if (this._isOpen) {
        this._unlockScroll();
        this._setPageInert(false);
        this._detachKeyHandlers();
        // Clean up nesting state so ancestors don't hold stale counts
        walkAncestors(this, (a) => a._decrementNested());
        drawerParents.delete(this);
      }
      this._portal?.remove();
      this._portal = null;
    }
  }

  // ─── DrawerTrigger ──────────────────────────────────────────────────────────
  //
  //  Wrap your <button> or <a> with this element.
  //  The click listener is intentionally shallow — it just calls open() on
  //  the nearest drawer-root ancestor. If your trigger lives outside the root,
  //  use the data-drawer-open="<id>" pattern instead (wired below).
  //

  class DrawerTrigger extends HTMLElement {
    connectedCallback() {
      this._onClick = () =>
        /** @type {DrawerRoot | null} */ (this.closest("drawer-root"))?.open();
      this.addEventListener("click", this._onClick);
    }
    disconnectedCallback() {
      if (this._onClick) {
        this.removeEventListener("click", this._onClick);
      }
    }
  }

  // ─── DrawerBackdrop ─────────────────────────────────────────────────────────

  class DrawerBackdrop extends HTMLElement {
    connectedCallback() {
      this.setAttribute("aria-hidden", "true");
      this._onClick = () => {
        const r = /** @type {DrawerRoot | undefined} */ (
          /** @type {any} */ (this)._root
        );
        if (r && !r.disablePointerDismissal) {
          r.close();
        }
      };
      this.addEventListener("click", this._onClick);
    }
    disconnectedCallback() {
      if (this._onClick) {
        this.removeEventListener("click", this._onClick);
      }
    }
  }

  // ─── DrawerPanel ────────────────────────────────────────────────────────────
  //
  //  The sliding sheet element. Handles:
  //    - ARIA dialog semantics
  //    - Touch/pointer swipe-to-dismiss (bottom sheet: swipe down)
  //    - Live CSS custom properties during gesture
  //    - ResizeObserver to keep --drawer-height in sync (used by nesting)
  //

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      if (!this.hasAttribute("tabindex")) {
        this.setAttribute("tabindex", "-1");
      }
      this._initSwipe();

      // Keep --drawer-height up to date so ancestor panels can calculate
      // their scale / peek transforms when this panel is the frontmost.
      this._resizeObserver = new ResizeObserver(() => {
        this.style.setProperty("--drawer-height", `${this.offsetHeight}px`);
      });
      this._resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this._destroySwipe();
      this._resizeObserver?.disconnect();
    }

    // ── Swipe-to-dismiss ────────────────────────────────────────────────────────

    _initSwipe() {
      // Initialise swipe state so TypeScript knows these are always numbers
      this._startY = 0;
      this._startX = 0;
      this._lastY = 0;
      this._lastX = 0;
      this._startTime = 0;
      this._swiping = false;
      /** @type {'x' | 'y' | null} */
      this._axis = null;

      this._onDown = this._handleDown.bind(this);
      this._onMove = this._handleMove.bind(this);
      this._onUp = this._handleUp.bind(this);

      const handle = this.querySelector("drawer-handle");
      if (!handle) {
        console.warn(
          "<drawer-panel>: No <drawer-handle> found — swipe-to-dismiss disabled."
        );
        return;
      }
      this._handle = handle;
      this._handle.addEventListener("pointerdown", this._onDown);
    }

    _destroySwipe() {
      this._handle?.removeEventListener("pointerdown", this._onDown);
      this._releasePointerListeners();
    }

    _releasePointerListeners() {
      document.removeEventListener("pointermove", this._onMove);
      document.removeEventListener("pointerup", this._onUp);
    }

    /** @param {PointerEvent} e */
    _handleDown(e) {
      // Primary pointer only
      if (e.button !== 0) {
        return;
      }
      // Respect swipe-ignore zones
      if (
        /** @type {Element} */ (e.target).closest("[data-drawer-swipe-ignore]")
      ) {
        return;
      }

      this._startY = e.clientY;
      this._startX = e.clientX;
      this._lastY = e.clientY;
      this._startTime = performance.now();
      this._axis = /** @type {'x' | 'y' | null} */ (null);
      this._swiping = false;

      document.addEventListener("pointermove", this._onMove);
      document.addEventListener("pointerup", this._onUp);
    }

    /** @param {PointerEvent} e */
    _handleMove(e) {
      const selfRoot = /** @type {DrawerRoot | undefined} */ (
        /** @type {any} */ (this)._root
      );
      const cfg = getDirectionConfig(selfRoot?.swipeDirection ?? null);

      const rawDy = e.clientY - (this._startY ?? 0);
      const rawDx = e.clientX - (this._startX ?? 0);

      // Determine gesture axis on first significant movement.
      // Abort if the user is dragging on the wrong axis for this drawer direction.
      if (!this._axis) {
        if (Math.abs(rawDy) < 5 && Math.abs(rawDx) < 5) {
          return;
        }
        this._axis = Math.abs(rawDy) >= Math.abs(rawDx) ? "y" : "x";
        if (this._axis !== cfg.axis) {
          this._releasePointerListeners();
          return;
        }
      }

      const rawDelta = cfg.axis === "y" ? rawDy : rawDx;
      // dismissDelta is positive when the user is swiping toward dismiss.
      const dismissDelta = rawDelta * -cfg.openSign;

      if (dismissDelta <= 0) {
        // Moving in the open direction — update last position and ignore.
        if (cfg.axis === "y") {
          this._lastY = e.clientY;
        } else {
          this._lastX = e.clientX;
        }
        return;
      }

      // For vertical drawers, defer to native scroll if content hasn't scrolled to top.
      if (cfg.axis === "y") {
        const scrollable = this._scrollableAncestor(
          /** @type {HTMLElement} */ (e.target)
        );
        if (scrollable && scrollable.scrollTop > 0) {
          return;
        }
      }

      // Activate swipe state on the first qualifying move.
      if (!this._swiping) {
        this._swiping = true;
        this.setAttribute("data-swiping", "");

        const parentRoot = selfRoot ? drawerParents.get(selfRoot) : undefined;
        if (parentRoot?._panel) {
          parentRoot._panel.setAttribute("data-nested-drawer-swiping", "");
        }

        try {
          this.setPointerCapture(e.pointerId);
        } catch (_) {
          /* ignore */
        }
      }

      if (cfg.axis === "y") {
        this._lastY = e.clientY;
      } else {
        this._lastX = e.clientX;
      }

      const panelSize = cfg.axis === "y" ? this.offsetHeight : this.offsetWidth;
      const movement = Math.max(0, dismissDelta);
      const progress = +Math.min(1, movement / (panelSize || 1)).toFixed(4);

      this.style.setProperty(cfg.cssVar, `${movement}px`);
      this.style.setProperty("--drawer-swipe-progress", String(progress));

      if (selfRoot?._backdrop) {
        selfRoot._backdrop.style.setProperty(
          "--drawer-swipe-progress",
          String(progress)
        );
      }
    }

    _handleUp() {
      this._releasePointerListeners();

      const selfRoot = /** @type {DrawerRoot | undefined} */ (
        /** @type {any} */ (this)._root
      );
      const parentRoot = selfRoot ? drawerParents.get(selfRoot) : undefined;
      if (parentRoot?._panel) {
        parentRoot._panel.removeAttribute("data-nested-drawer-swiping");
      }

      if (!this._swiping) {
        return;
      }

      const cfg = getDirectionConfig(selfRoot?.swipeDirection ?? null);
      const rawDelta =
        cfg.axis === "y"
          ? (this._lastY ?? 0) - (this._startY ?? 0)
          : (this._lastX ?? 0) - (this._startX ?? 0);
      const dismissDelta = rawDelta * -cfg.openSign;

      const dt = Math.max(1, performance.now() - this._startTime);
      const velocity = dismissDelta / dt; // px/ms

      const panelSize = cfg.axis === "y" ? this.offsetHeight : this.offsetWidth;
      const progress = dismissDelta / (panelSize || 1);
      const shouldDismiss =
        progress > 0.35 || (velocity > 0.4 && dismissDelta > 40);

      if (shouldDismiss) {
        const strength = +Math.min(1, velocity / 1.5).toFixed(4);
        const swipeDuration = strength * 400; // ms

        this.style.setProperty("--drawer-swipe-strength", String(strength));
        if (selfRoot?._backdrop) {
          selfRoot._backdrop.style.setProperty(
            "--drawer-swipe-strength",
            String(strength)
          );
        }

        selfRoot?._close({ swipeDuration });
      } else {
        this.removeAttribute("data-swiping");
        this._swiping = false;
        selfRoot?._clearSwipeProps();
      }
    }

    /**
     * Walk up the DOM from `el` to find an overflow-scrollable parent inside the panel.
     * @param {HTMLElement} el
     * @returns {HTMLElement | null}
     */
    _scrollableAncestor(el) {
      while (el && el !== this) {
        const oy = getComputedStyle(el).overflowY;
        if (
          (oy === "auto" || oy === "scroll") &&
          el.scrollHeight > el.clientHeight
        ) {
          return el;
        }
        el = /** @type {HTMLElement} */ (el.parentElement);
      }
      return null;
    }
  }

  // ─── DrawerHandle ────────────────────────────────────────────────────────────
  //
  //  Drag target for swipe-to-dismiss. Place as the first child of <drawer-panel>.
  //  Sets touch-action:none so the browser surrenders the gesture to JS.
  //  DrawerPanel._initSwipe() attaches the pointerdown listener here; without
  //  this element swipe-to-dismiss is silently disabled.
  //

  class DrawerHandle extends HTMLElement {
    connectedCallback() {
      // Behaviour, not style: prevent the browser from claiming this touch
      // as a scroll gesture before JS can evaluate it as a dismiss swipe.
      this.style.touchAction = "none";
    }
  }

  // ─── DrawerTitle / DrawerDescription ────────────────────────────────────────
  //
  //  Semantic wrappers. Their IDs are generated and wired to the panel's
  //  aria-labelledby / aria-describedby by DrawerRoot._init().
  //

  class DrawerTitle extends HTMLElement {}
  class DrawerDescription extends HTMLElement {}

  // ─── DrawerClose ────────────────────────────────────────────────────────────
  //
  //  Wrap your close <button>. Can live anywhere inside <drawer-panel>.
  //  Uses closest('drawer-panel')._root to find the controller,
  //  because the panel has been moved into the portal.
  //

  class DrawerClose extends HTMLElement {
    connectedCallback() {
      this._onClick = () => {
        const panel = /** @type {any} */ (this.closest("drawer-panel"));
        /** @type {DrawerRoot | undefined} */ (panel?._root)?.close();
      };
      this.addEventListener("click", this._onClick);
    }
    disconnectedCallback() {
      if (this._onClick) {
        this.removeEventListener("click", this._onClick);
      }
    }
  }

  // ─── DrawerSwipeArea ────────────────────────────────────────────────────────
  //
  //  Stationary hit target placed at the screen edge. Lets users swipe-to-open
  //  a drawer before it is visible. Does NOT portal — stays in authored position.
  //
  //  CSS example (bottom sheet):
  //    drawer-swipe-area {
  //      position: fixed; inset-inline: 0; bottom: 0;
  //      height: 1.5rem; z-index: 100;
  //      cursor: grab; touch-action: none;
  //    }
  //

  class DrawerSwipeArea extends HTMLElement {
    connectedCallback() {
      this._startX = 0;
      this._startY = 0;
      this._lastX = 0;
      this._lastY = 0;
      this._startTime = 0;
      /** @type {'x' | 'y' | null} */
      this._axis = null;

      this._onDown = this._handleDown.bind(this);
      this._onMove = this._handleMove.bind(this);
      this._onUp = this._handleUp.bind(this);
      this.addEventListener("pointerdown", this._onDown);
    }

    disconnectedCallback() {
      if (this._onDown) {
        this.removeEventListener("pointerdown", this._onDown);
      }
      this._releaseListeners();
    }

    _releaseListeners() {
      document.removeEventListener("pointermove", this._onMove);
      document.removeEventListener("pointerup", this._onUp);
    }

    /** @param {PointerEvent} e */
    _handleDown(e) {
      if (e.button !== 0) {
        return;
      }
      const root = /** @type {DrawerRoot | null} */ (
        this.closest("drawer-root")
      );
      if (!root || root.isOpen || root._isAnimating || root._isPeeking) {
        return;
      }

      this._startX = e.clientX;
      this._startY = e.clientY;
      this._lastX = e.clientX;
      this._lastY = e.clientY;
      this._startTime = performance.now();
      this._axis = null;

      try {
        this.setPointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }

      document.addEventListener("pointermove", this._onMove);
      document.addEventListener("pointerup", this._onUp);
    }

    /** @param {PointerEvent} e */
    _handleMove(e) {
      const root = /** @type {DrawerRoot | null} */ (
        this.closest("drawer-root")
      );
      if (!root) {
        return;
      }

      const cfg = getDirectionConfig(root.swipeDirection ?? null);
      const rawDy = e.clientY - (this._startY ?? 0);
      const rawDx = e.clientX - (this._startX ?? 0);

      // Determine axis on first significant movement; abort if wrong axis.
      if (!this._axis) {
        if (Math.abs(rawDy) < 5 && Math.abs(rawDx) < 5) {
          return;
        }
        this._axis = Math.abs(rawDy) >= Math.abs(rawDx) ? "y" : "x";
        if (this._axis !== cfg.axis) {
          this._releaseListeners();
          return;
        }
      }

      this._lastX = e.clientX;
      this._lastY = e.clientY;

      const rawDelta = cfg.axis === "y" ? rawDy : rawDx;
      root._peekOpen(rawDelta);
    }

    _handleUp() {
      this._releaseListeners();

      const root = /** @type {DrawerRoot | null} */ (
        this.closest("drawer-root")
      );
      if (!(root && root._isPeeking)) {
        return;
      }

      const cfg = getDirectionConfig(root.swipeDirection ?? null);
      const rawDelta =
        cfg.axis === "y"
          ? (this._lastY ?? 0) - (this._startY ?? 0)
          : (this._lastX ?? 0) - (this._startX ?? 0);
      const openMovement = rawDelta * cfg.openSign;
      const dt = Math.max(1, performance.now() - (this._startTime ?? 0));
      const velocity = openMovement / dt;

      root._commitOpen(velocity, openMovement);
    }
  }

  // ─── Global data-drawer-open delegate ───────────────────────────────────────
  //
  //  Lets any element anywhere on the page open a drawer by ID without
  //  needing to be inside <drawer-root>. Useful for header cart buttons,
  //  Liquid section blocks, etc.
  //
  //    <button data-drawer-open="cart-drawer" type="button">Open Cart</button>
  //

  document.addEventListener("click", (e) => {
    const target = /** @type {Element} */ (e.target);
    if (!target) {
      return;
    }
    const btn = target.closest("[data-drawer-open]");
    if (!btn) {
      return;
    }
    const id = btn.getAttribute("data-drawer-open");
    const drawer = /** @type {DrawerRoot | null} */ (
      id ? document.getElementById(id) : null
    );
    if (drawer?.open) {
      drawer.open();
    }
  });

  // ─── Register all elements ───────────────────────────────────────────────────

  /** @type {[string, CustomElementConstructor][]} */
  const elements = [
    ["drawer-root", DrawerRoot],
    ["drawer-trigger", DrawerTrigger],
    ["drawer-backdrop", DrawerBackdrop],
    ["drawer-panel", DrawerPanel],
    ["drawer-swipe-area", DrawerSwipeArea],
    ["drawer-handle", DrawerHandle],
    ["drawer-title", DrawerTitle],
    ["drawer-description", DrawerDescription],
    ["drawer-close", DrawerClose],
  ];

  elements.forEach(([name, Cls]) => {
    if (!customElements.get(name)) {
      customElements.define(name, Cls);
    }
  });
})();
